import { useAuth } from "@/hooks/useAuth";
import { Navigate } from "react-router-dom";
import { logger } from "@/lib/logger";
import { useLegalAcceptanceCheck } from "@/hooks/useLegalAcceptanceCheck";
import { Skeleton } from "@/components/shared/ui/skeleton";

type ProtectedRouteProps = {
  children: React.ReactNode;
};

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, profile, loading } = useAuth();
  const { needsAcceptance, loading: legalLoading } = useLegalAcceptanceCheck();
  const path = typeof window !== "undefined" ? window.location.pathname : "";

  if (loading || legalLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="space-y-3 w-48">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    );
  }

  if (!user || !profile) {
    logger.debug("Redirecting to login - no user or profile");
    return <Navigate to="/login" replace />;
  }

  // Redirect to /setup-password when password change is required
  // This covers:
  //   1. Newly invited users via the old OTP flow (requires_password_setup)
  //   2. Newly created accounts with a temporary password (must_change_password)
  const isOnSetupPasswordPage = window.location.pathname === "/setup-password";
  const needsPasswordChange =
    profile.requires_password_setup === true ||
    profile.must_change_password === true;
  if (needsPasswordChange && !isOnSetupPasswordPage) {
    logger.debug("User requires password setup, redirecting to setup-password");
    return <Navigate to="/setup-password" replace />;
  }

  // Require acceptance of latest legal docs
  // Allow access to /terms page itself and /reset-password
  if (needsAcceptance && path !== "/terms" && path !== "/reset-password") {
    logger.debug("User needs legal acceptance, redirecting to /terms");
    return <Navigate to="/terms" replace />;
  }

  return <>{children}</>;
};
